/**
 * Super Inteligente Order Tracking
 * Real-time multi-store pickup progress, item statuses, driver location
 */

import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Package, MapPin, Truck, Check, X, Clock, Phone, Store, AlertTriangle, ChevronLeft, RefreshCw, ShoppingCart, Home } from 'lucide-react';
import { Helmet } from 'react-helmet-async';
import { MarketplaceHeader, MarketplaceFooter, MobileBottomNav } from '../../components/marketplace/MarketplaceLayout';
import api from '../../services/api';

const STATUS_CONFIG = {
  pending: { label: 'Procesando', icon: '⏳', color: 'text-amber-600', bg: 'bg-amber-50', desc: 'Estamos procesando tu pedido' },
  stores_notified: { label: 'Tiendas notificadas', icon: '🔔', color: 'text-blue-600', bg: 'bg-blue-50', desc: 'Las tiendas están preparando tus productos' },
  driver_assigned: { label: 'Repartidor asignado', icon: '🏍️', color: 'text-purple-600', bg: 'bg-purple-50', desc: 'Un repartidor va en camino a recoger' },
  picking_up: { label: 'Recogiendo productos', icon: '🛒', color: 'text-emerald-600', bg: 'bg-emerald-50', desc: 'El repartidor está recogiendo en las tiendas' },
  delivering: { label: 'En camino', icon: '🚀', color: 'text-emerald-600', bg: 'bg-emerald-50', desc: 'Tu pedido va en camino a tu domicilio' },
  delivered: { label: 'Entregado', icon: '✅', color: 'text-green-600', bg: 'bg-green-50', desc: '¡Tu pedido fue entregado!' },
  cancelled: { label: 'Cancelado', icon: '❌', color: 'text-red-600', bg: 'bg-red-50', desc: 'Este pedido fue cancelado' },
};

const ITEM_STATUS_LABELS = {
  pending: { label: 'Pendiente', color: 'bg-gray-100 text-gray-600' },
  assigned: { label: 'Asignado', color: 'bg-blue-100 text-blue-600' },
  picked_up: { label: 'Recogido', color: 'bg-green-100 text-green-700' },
  not_found: { label: 'No encontrado', color: 'bg-amber-100 text-amber-700' },
  rerouted: { label: 'Buscando en otra tienda', color: 'bg-blue-100 text-blue-600' },
  cancelled: { label: 'Cancelado', color: 'bg-red-100 text-red-600' },
};

const SuperOrderTracking = () => {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const pollRef = useRef(null);

  useEffect(() => {
    loadOrder();
    // Poll every 10 seconds
    pollRef.current = setInterval(loadOrder, 10000);
    return () => clearInterval(pollRef.current);
  }, [orderId]);

  const loadOrder = async () => {
    try {
      const res = await api.get(`/super-inteligente/orders/${orderId}`);
      setOrder(res.data);
      // Stop polling and clear active order if delivered or cancelled
      if (['delivered', 'cancelled'].includes(res.data.status)) {
        localStorage.removeItem('super_active_order');
        clearInterval(pollRef.current);
      }
    } catch (e) {
      console.error(e);
    } finally { setLoading(false); }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <MarketplaceHeader />
        <div className="flex justify-center py-32">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600"></div>
        </div>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="min-h-screen bg-gray-50">
        <MarketplaceHeader />
        <div className="text-center py-32">
          <p className="text-gray-500 text-lg">Pedido no encontrado</p>
          <Link to="/super" className="text-emerald-600 font-medium mt-4 inline-block">Volver al Super</Link>
        </div>
      </div>
    );
  }

  const status = STATUS_CONFIG[order.status] || STATUS_CONFIG.pending;
  const stores = order.store_assignments || [];
  const items = order.items || [];
  const itemStatuses = order.item_statuses || {};

  // Progress steps
  const progressSteps = [
    { key: 'stores_notified', label: 'Tiendas notificadas' },
    { key: 'driver_assigned', label: 'Repartidor asignado' },
    { key: 'picking_up', label: 'Recogiendo' },
    { key: 'delivering', label: 'En camino' },
    { key: 'delivered', label: 'Entregado' },
  ];
  const currentStepIndex = progressSteps.findIndex(s => s.key === order.status);

  return (
    <div className="min-h-screen bg-gray-50">
      <MarketplaceHeader />

      {/* Header */}
      <Helmet>
        <title>Seguimiento de Pedido {order?.order_number || ''} | Super Inteligente</title>
        <meta name="robots" content="noindex" />
      </Helmet>
      <div className="bg-[#0a1628] text-white py-6 px-4">
        <div className="max-w-2xl mx-auto">
          <div className="flex items-center gap-3 mb-4">
            <button onClick={() => navigate('/super')} className="p-2 hover:bg-white/10 rounded-xl">
              <ChevronLeft className="w-5 h-5" />
            </button>
            <div>
              <h1 className="text-lg font-extrabold">Pedido {order.order_number}</h1>
              <p className="text-xs text-white/50">{new Date(order.created_at).toLocaleString('es-MX')}</p>
            </div>
          </div>

          {/* Status Badge */}
          <div className={`${status.bg} rounded-xl p-4 flex items-center gap-3`}>
            <span className="text-3xl">{status.icon}</span>
            <div>
              <p className={`font-bold ${status.color}`}>{status.label}</p>
              <p className="text-sm text-gray-600">{status.desc}</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-4 py-6 space-y-6 pb-32">

        {/* Progress Bar */}
        <div className="bg-white rounded-2xl border border-gray-100 p-5">
          <div className="flex items-center justify-between mb-2">
            {progressSteps.map((step, i) => (
              <React.Fragment key={step.key}>
                <div className="flex flex-col items-center">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold ${
                    i <= currentStepIndex ? 'bg-emerald-600 text-white' : 'bg-gray-200 text-gray-500'
                  }`}>
                    {i <= currentStepIndex ? <Check className="w-4 h-4" /> : i + 1}
                  </div>
                  <span className="text-[10px] text-gray-500 mt-1 text-center max-w-[60px] leading-tight">{step.label}</span>
                </div>
                {i < progressSteps.length - 1 && (
                  <div className={`flex-1 h-1 mx-1 rounded ${i < currentStepIndex ? 'bg-emerald-600' : 'bg-gray-200'}`} />
                )}
              </React.Fragment>
            ))}
          </div>
        </div>

        {/* Driver Info */}
        {order.driver && (
          <div className="bg-white rounded-2xl border border-gray-100 p-5">
            <h3 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
              <Truck className="w-5 h-5 text-emerald-600" /> Tu repartidor
            </h3>
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 bg-emerald-100 rounded-full flex items-center justify-center">
                <span className="text-2xl">🏍️</span>
              </div>
              <div>
                <p className="font-bold text-gray-900">{order.driver.full_name}</p>
                <p className="text-sm text-gray-500">{order.driver.vehicle_type}</p>
              </div>
              {order.driver.phone && (
                <a href={`tel:${order.driver.phone}`} className="ml-auto bg-emerald-100 p-3 rounded-xl hover:bg-emerald-200 transition">
                  <Phone className="w-5 h-5 text-emerald-700" />
                </a>
              )}
            </div>
          </div>
        )}

        {/* Store Progress */}
        {stores.length > 0 && (
          <div className="bg-white rounded-2xl border border-gray-100 p-5">
            <h3 className="font-bold text-gray-900 mb-4 flex items-center gap-2">
              <Store className="w-5 h-5 text-emerald-600" /> Recogida en {stores.length} {stores.length === 1 ? 'tienda' : 'tiendas'}
            </h3>
            <div className="space-y-3">
              {stores.map((store, i) => (
                <div key={i} className={`flex items-center gap-3 p-3 rounded-xl ${store.picked_up ? 'bg-green-50' : 'bg-gray-50'}`}>
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                    store.picked_up ? 'bg-green-600 text-white' : 'bg-gray-300 text-white'
                  }`}>
                    {store.picked_up ? <Check className="w-4 h-4" /> : i + 1}
                  </div>
                  <div className="flex-1">
                    <p className="font-medium text-gray-900 text-sm">{store.branch_name || `Tienda ${i + 1}`}</p>
                    <p className="text-xs text-gray-500">{store.items?.length || 0} productos</p>
                  </div>
                  <span className={`text-xs font-medium px-2 py-1 rounded-full ${
                    store.picked_up ? 'bg-green-100 text-green-700' : 'bg-gray-200 text-gray-600'
                  }`}>
                    {store.picked_up ? 'Recogido' : 'Pendiente'}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Items List */}
        <div className="bg-white rounded-2xl border border-gray-100 p-5">
          <h3 className="font-bold text-gray-900 mb-4 flex items-center gap-2">
            <Package className="w-5 h-5 text-emerald-600" /> Productos ({items.length})
          </h3>
          <div className="space-y-2">
            {items.map((item, i) => {
              const itemStatus = itemStatuses[item.catalog_product_id];
              const statusLabel = itemStatus ? (ITEM_STATUS_LABELS[itemStatus.status] || ITEM_STATUS_LABELS.pending) : ITEM_STATUS_LABELS.pending;
              const isCancelled = itemStatus?.status === 'cancelled';
              return (
                <div key={i} className={`flex items-center gap-3 p-3 rounded-xl ${isCancelled ? 'bg-red-50 opacity-60' : 'bg-gray-50'}`}>
                  {item.image_url && <img src={item.image_url} alt="" className="w-10 h-10 rounded-lg object-cover flex-shrink-0" />}
                  <div className="flex-1 min-w-0">
                    <p className={`text-sm font-medium ${isCancelled ? 'line-through text-gray-500' : 'text-gray-900'}`}>{item.name}</p>
                    <p className="text-xs text-gray-500">{item.quantity} {item.unit || 'pz'} · ${item.line_total?.toFixed(2)}</p>
                  </div>
                  <span className={`text-[10px] font-semibold px-2 py-1 rounded-full whitespace-nowrap ${statusLabel.color}`}>
                    {statusLabel.label}
                  </span>
                </div>
              );
            })}
          </div>

          {/* Unavailable items */}
          {order.unavailable_items?.length > 0 && (
            <div className="mt-3 bg-amber-50 rounded-xl p-3 flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
              <p className="text-xs text-amber-700">{order.unavailable_items.length} producto(s) no estaban disponibles al momento del pedido.</p>
            </div>
          )}
        </div>

        {/* Order Summary */}
        <div className="bg-white rounded-2xl border border-gray-100 p-5">
          <h3 className="font-bold text-gray-900 mb-3">Resumen</h3>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-500">Subtotal productos</span>
              <span className="font-medium">${order.subtotal?.toFixed(2)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">Envío ({order.delivery_breakdown?.vehicle_type === 'car' ? 'automóvil' : 'motocicleta'})</span>
              <span className="font-medium">${order.delivery_breakdown?.vehicle_fee?.toFixed(2) || order.delivery_fee?.toFixed(2)}</span>
            </div>
            {order.delivery_breakdown?.service_fee > 0 && (
              <div className="flex justify-between text-gray-400">
                <span>Cargo por servicio</span>
                <span>${order.delivery_breakdown.service_fee.toFixed(2)}</span>
              </div>
            )}
            {order.refund_amount > 0 && (
              <div className="flex justify-between text-red-600">
                <span>Reembolso por items cancelados</span>
                <span className="font-medium">-${order.refund_amount.toFixed(2)}</span>
              </div>
            )}
            <div className="flex justify-between pt-2 border-t border-gray-100">
              <span className="font-bold text-gray-900">Total</span>
              <span className="text-xl font-extrabold text-emerald-600">${order.total?.toFixed(2)}</span>
            </div>
          </div>
          <div className="mt-3 text-xs text-gray-400">
            Pago: {order.payment_method === 'cash_on_delivery' ? '💵 Efectivo al recibir' : '💳 Tarjeta al recibir'}
          </div>
        </div>

        {/* Delivery Address */}
        <div className="bg-white rounded-2xl border border-gray-100 p-5">
          <h3 className="font-bold text-gray-900 mb-2 flex items-center gap-2">
            <Home className="w-5 h-5 text-emerald-600" /> Entregar en
          </h3>
          <p className="text-sm text-gray-700">{order.customer_info?.address}</p>
          {order.customer_info?.references && (
            <p className="text-xs text-gray-500 mt-1">Ref: {order.customer_info.references}</p>
          )}
        </div>

        {/* Impact Message */}
        {order.status === 'delivered' && (
          <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-5 text-center">
            <span className="text-4xl">🎉</span>
            <p className="font-bold text-emerald-900 mt-2 text-lg">¡Gracias por tu compra!</p>
            <p className="text-sm text-emerald-700 mt-1">Apoyaste a {stores.length} tienda{stores.length > 1 ? 's' : ''} de tu comunidad</p>
            <Link to="/super" className="inline-block mt-4 bg-emerald-600 text-white px-6 py-3 rounded-xl font-bold hover:bg-emerald-700 transition">
              Comprar de nuevo
            </Link>
          </div>
        )}
      </div>

      <MarketplaceFooter />
      <MobileBottomNav />
    </div>
  );
};

export default SuperOrderTracking;
